import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styles from './CustomSelect.module.css';

interface Option {
    value: string | number;
    label: string;
    count?: number;
}

interface CustomSelectProps {
    id: string;
    value: string | number;
    onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    options: Option[];
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    style?: React.CSSProperties;
    label?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
    id,
    value,
    onChange,
    options,
    placeholder = 'Selecione...',
    disabled = false,
    className = '',
    style,
    label
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [selectedOption, setSelectedOption] = useState<Option | null>(
        options.find(opt => String(opt.value) === String(value)) || null
    );
    const selectRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
                const target = event.target as Element;
                if (!target.closest(`.${styles.optionsContainer}`)) {
                    setIsOpen(false);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    React.useLayoutEffect(() => {
        const updatePosition = () => {
            if (isOpen && selectRef.current) {
                const rect = selectRef.current.getBoundingClientRect();
                
                const spaceBelow = window.innerHeight - rect.bottom;
                const dropdownHeight = 250; 
                
                let topPos = rect.bottom + 8;
                if (spaceBelow < dropdownHeight && rect.top > dropdownHeight) {
                    topPos = rect.top - dropdownHeight - 8;
                }
                
                setDropdownStyle({
                    position: 'fixed',
                    top: spaceBelow < dropdownHeight && rect.top > dropdownHeight ? 'auto' : rect.bottom + 8,
                    bottom: spaceBelow < dropdownHeight && rect.top > dropdownHeight ? window.innerHeight - rect.top + 8 : 'auto',
                    left: rect.left,
                    right: 'auto',
                    width: rect.width,
                    zIndex: 99999
                });
            }
        };

        if (isOpen) {
            updatePosition();
            window.addEventListener('scroll', updatePosition, true);
            window.addEventListener('resize', updatePosition);
        }

        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen]);

    useEffect(() => {
        const option = options.find(opt => String(opt.value) === String(value));
        setSelectedOption(option || null);
    }, [value, options]);

    useEffect(() => {
        if (isOpen) {
            const currentIdx = options.findIndex(opt => String(opt.value) === String(value));
            setActiveIndex(currentIdx !== -1 ? currentIdx : -1);
        } else {
            setActiveIndex(-1);
        }
    }, [isOpen, options, value]);

    const scrollToIndex = (index: number) => {
        if (!listRef.current) return;
        const item = listRef.current.children[index] as HTMLElement;
        if (item) {
            item.scrollIntoView({ block: 'nearest' });
        }
    };

    const handleSelect = (option: Option) => {
        setSelectedOption(option);
        setIsOpen(false);

        const event = {
            target: {
                value: String(option.value),
                id
            }
        } as React.ChangeEvent<HTMLSelectElement>;

        onChange(event);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (disabled) return;

        if (!isOpen) {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                e.preventDefault();
                setIsOpen(true);
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActiveIndex(prev => {
                    const next = prev < options.length - 1 ? prev + 1 : prev;
                    scrollToIndex(next);
                    return next;
                });
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActiveIndex(prev => {
                    const next = prev > 0 ? prev - 1 : 0;
                    scrollToIndex(next);
                    return next;
                });
                break;
            case 'Enter':
            case ' ':
                e.preventDefault();
                if (activeIndex >= 0 && activeIndex < options.length) {
                    handleSelect(options[activeIndex]);
                }
                break;
            case 'Escape':
            case 'Tab':
                setIsOpen(false);
                break;
        }
    };

    const wrapperStyle: React.CSSProperties = { ...style };
    const triggerStyle: React.CSSProperties = {};

    if (style) {
        if (style.height) {
            triggerStyle.height = '100%';
            triggerStyle.minHeight = 'unset';
            triggerStyle.borderRadius = '6px';
        }
        if (style.fontSize) {
            triggerStyle.fontSize = style.fontSize;
        }
        if (style.padding) {
            triggerStyle.padding = style.padding;
            delete wrapperStyle.padding;
        }
    }

    return (
        <div 
            className={`${styles.customSelect} ${disabled ? styles.disabled : ''}`} 
            ref={selectRef}
            onKeyDown={handleKeyDown}
            tabIndex={disabled ? -1 : 0}
            style={wrapperStyle}
        >
            <div
                className={`${styles.selectTrigger} ${isOpen ? styles.open : ''} ${className}`}
                onClick={() => !disabled && setIsOpen(!isOpen)}
                style={triggerStyle}
            >
                <span className={styles.selectValue}>
                    {label && <span className={styles.labelPrefix}>{label}:</span>}
                    {selectedOption ? (
                        <>
                            <span className={styles.optionLabel}>{selectedOption.label}</span>
                            {selectedOption.count !== undefined && (
                                <span className={styles.badge}>{selectedOption.count}</span>
                            )}
                        </>
                    ) : (
                        placeholder
                    )}
                </span>
                <svg className={styles.arrow} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5" style={{ width: '12px', height: '12px' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                </svg>
            </div>

            {isOpen && ReactDOM.createPortal(
                <div className={styles.optionsContainer} style={{ ...dropdownStyle, maxHeight: '250px', overflowY: 'auto' }} ref={listRef}>
                    {options.map((option, index) => (
                        <div
                            key={`${option.value}-${index}`}
                            className={`${styles.option} ${
                                String(option.value) === String(value) ? styles.selected : ''
                            } ${index === activeIndex ? styles.active : ''}`}
                            onClick={(e) => { e.stopPropagation(); handleSelect(option); }}
                        >
                            <span className={styles.optionLabel}>{option.label}</span>
                            {option.count !== undefined && (
                                <span className={styles.badge}>{option.count}</span>
                            )}
                        </div>
                    ))}
                </div>,
                document.body
            )}
        </div>
    );
};

export default CustomSelect;
